import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import { Calendar, Tag, ArrowLeft, Globe, Eye, FileText } from "lucide-react";
import { Panel, PageHeader } from "@/components/admin-shell";

interface IBlogPost {
  _id: string;
  slug: string;
  title: { bn: string; en: string };
  excerpt: { bn: string; en: string };
  content: { bn: string; en: string };
  category: { bn: string; en: string };
  cover: string;
  date: string;
  featured: boolean;
  published: boolean;
}

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [lang, setLang] = useState<"en" | "bn">("en");

  // Fetch the single post details by slug
  const {
    data: post,
    isLoading,
    error,
  } = useQuery<IBlogPost>({
    queryKey: ["post", slug],
    queryFn: () => apiRequest<IBlogPost>(`/posts/${slug}`),
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-gold">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" />
          <span className="text-sm font-medium tracking-wide">Loading article preview...</span>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="text-center py-12">
        <h2 className="text-red-400 font-display text-2xl mb-2">Error Loading Post</h2>
        <p className="text-muted-foreground mb-6">
          {(error as Error)?.message || "The requested blog post could not be found."}
        </p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 rounded-md border border-gold/30 px-4 py-2 text-sm text-gold hover:bg-accent transition"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Blog
        </Link>
      </div>
    );
  }

  // Split content by newlines to render paragraphs cleanly
  const paragraphs = post.content[lang]
    ? post.content[lang].split("\n").filter((p) => p.trim() !== "")
    : [];

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gold-soft hover:text-gold transition"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Blog Management
        </Link>

        {/* Bilingual Preview Switcher */}
        <div className="flex items-center gap-1.5 rounded-lg border border-gold/20 bg-accent/15 p-1">
          <button
            onClick={() => setLang("en")}
            className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
              lang === "en"
                ? "bg-gold text-primary-foreground shadow"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Globe className="h-3.5 w-3.5" /> English View
          </button>
          <button
            onClick={() => setLang("bn")}
            className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
              lang === "bn"
                ? "bg-gold text-primary-foreground shadow"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Globe className="h-3.5 w-3.5" /> বাংলা সংস্করণ
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Blog Post Main Article (similar to client UI) */}
        <div className="lg:col-span-3">
          <article className="rounded-2xl border border-gold/20 bg-brand-card p-6 sm:p-10 shadow-xl overflow-hidden page-transition">
            {/* Header section */}
            <div className="max-w-3xl mx-auto mb-8">
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                {post.category[lang] || post.category.en}
              </span>
              <h1 className="mt-4 font-display text-3xl sm:text-5xl text-foreground leading-tight font-bold">
                {post.title[lang] || post.title.en}
              </h1>
              <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-gold-soft" />
                  {new Date(post.date).toLocaleDateString(lang === "bn" ? "bn-BD" : "en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
                <span>•</span>
                <span
                  className={`rounded-full px-2.5 py-0.5 border text-[10px] uppercase font-semibold ${
                    post.published
                      ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                      : "bg-amber-500/10 text-amber-300 border-amber-500/30"
                  }`}
                >
                  {post.published ? "Published" : "Draft"}
                </span>
              </div>
            </div>

            {/* Cover Image */}
            <div className="mb-10 overflow-hidden rounded-xl border border-gold/25 shadow-lg">
              <img
                src={post.cover}
                alt={post.title[lang] || post.title.en}
                className="w-full h-[280px] sm:h-[420px] object-cover hover:scale-[1.02] transition-transform duration-700"
              />
            </div>

            {/* Content body */}
            <div className="max-w-3xl mx-auto space-y-6 text-foreground/90 leading-relaxed font-sans text-base">
              {/* Excerpt */}
              {post.excerpt[lang] && (
                <p className="text-lg sm:text-xl font-medium text-gold-soft border-l-2 border-gold/30 pl-4 py-1 italic">
                  {post.excerpt[lang]}
                </p>
              )}

              {/* Main Content */}
              {post.content[lang] ? (
                <div
                  className="prose-editor"
                  dangerouslySetInnerHTML={{ __html: post.content[lang] }}
                />
              ) : (
                <p className="text-muted-foreground italic">
                  No content available in this language.
                </p>
              )}
            </div>

            <div className="ornate-divider my-10" />

            {/* Branding details */}
            <div className="max-w-3xl mx-auto text-center text-xs text-muted-foreground italic font-display">
              সুবাসঘরের প্রতিটি গল্প একটি যাত্রা — কারিগর থেকে আপনার ত্বক পর্যন্ত।
            </div>
          </article>
        </div>

        {/* Sidebar Info & Controls */}
        <div className="space-y-6">
          <Panel title="Article Details">
            <div className="space-y-4 text-sm">
              <div>
                <span className="block text-[10px] uppercase tracking-wider text-gold-soft mb-0.5">
                  Permanent URL Slug
                </span>
                <span
                  className="font-mono text-xs text-foreground bg-accent/20 px-2 py-1 rounded block truncate"
                  title={post.slug}
                >
                  /blog/{post.slug}
                </span>
              </div>

              <div>
                <span className="block text-[10px] uppercase tracking-wider text-gold-soft mb-0.5">
                  English Title
                </span>
                <span
                  className="text-xs text-foreground font-medium block truncate"
                  title={post.title.en}
                >
                  {post.title.en}
                </span>
              </div>

              <div>
                <span className="block text-[10px] uppercase tracking-wider text-gold-soft mb-0.5">
                  Bengali Title
                </span>
                <span
                  className="text-xs text-foreground font-medium block truncate"
                  title={post.title.bn}
                >
                  {post.title.bn}
                </span>
              </div>

              <div className="ornate-divider my-2" />

              <div className="flex justify-between items-center text-xs">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Tag className="h-3.5 w-3.5 text-gold-soft" /> Category
                </span>
                <span className="font-semibold text-foreground">{post.category.en}</span>
              </div>

              <div className="flex justify-between items-center text-xs">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5 text-gold-soft" /> Featured Status
                </span>
                <span
                  className={`font-semibold ${post.featured ? "text-gold" : "text-muted-foreground"}`}
                >
                  {post.featured ? "Featured" : "Regular"}
                </span>
              </div>

              <div className="flex justify-between items-center text-xs">
                <span className="text-muted-foreground flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5 text-gold-soft" /> Post ID
                </span>
                <span className="font-mono text-[10px] text-muted-foreground truncate max-w-[120px]">
                  {post._id}
                </span>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
